import type { Solution } from 'api/contentful/contentTypes.cloverdotcom';
import { useEffect, useState } from 'react';
import type { FlatEntry } from 'services/cms/flattenContentful';
import { flattenObject } from 'utils/Object';
import { useLocalStorage } from './useLocalStorage';

type SolutionDiffType = {
  hasChange: boolean;
  solution: FlatEntry<Solution> | undefined;
};

const LOCAL_STORAGE_KEY = 'dcsAbTestOverrides';

/**
 * Takes a solution object and replaces any DCS IDs for provided bundles
 * that match an active A/B test
 */
export const useABSolution = (solutionEntry?: FlatEntry<Solution>) => {
  const storedOverrides = useLocalStorage(LOCAL_STORAGE_KEY, { isJSON: true });
  const [overrides, setOverrides] = useState(updateOverrides);
  const [solution, setSolution] = useState<FlatEntry<Solution> | undefined>(() => {
    if (!overrides || !solutionEntry) return solutionEntry;
    const solutionOverrides = getSolutionOverrides(undefined);
    return solutionOverrides.solution;
  });

  // If storedOverrides updates, update overrides
  useEffect(() => {
    const newOverrides = updateOverrides();
    if (JSON.stringify(newOverrides) !== JSON.stringify(overrides)) {
      setOverrides(newOverrides);
    }
  }, [storedOverrides]);

  // If overrides updates, update solution
  useEffect(() => {
    if (!solution || !solution.bundles) return;

    const solutionOverrides = getSolutionOverrides(solution);
    if (solutionOverrides.hasChange) {
      setSolution(solutionOverrides.solution);
    }
  }, [overrides]);

  // Utility function to flatten stored overrides
  function updateOverrides() {
    if (!storedOverrides) return {};
    return flattenObject(storedOverrides, false);
  }

  // Get overrides for the provided solution
  function getSolutionOverrides(initialValue: FlatEntry<Solution> | undefined): SolutionDiffType {
    if (!solutionEntry || !solutionEntry.bundles) {
      return {
        hasChange: false,
        solution: solutionEntry,
      };
    }

    const newSolution = { ...solutionEntry };
    newSolution.bundles = newSolution.bundles?.map(bundle => {
      if (!bundle.dcsId) return bundle;

      const overrideId = overrides[bundle.dcsId];
      if (overrideId && bundle.dcsId !== overrideId) {
        return {
          ...bundle,
          dcsId: overrideId,
        };
      }

      return bundle;
    });

    const hasChange = JSON.stringify(newSolution) !== JSON.stringify(initialValue);
    return {
      hasChange,
      solution: newSolution,
    };
  }

  return solution;
};
